ㅇㅇ<template>
  <div class="index-page">
    <!-- Hero -->
    <v-img :src="tableImage" cover height="520" class="hero">
      <div class="hero__overlay d-flex flex-column align-center justify-center text-center px-6">
        <img src="../assets/images/hypehive-logo.png" 
     height="60" 
     class="mb-3"
     style="filter: brightness(0) invert(1);">
        <h1 class="text-h4 text-sm-h3 text-md-h2 font-weight-bold mb-3">재가장기요양센터 업무, 더 단순하게</h1>
        <p class="text-body-1 text-sm-h6 mb-10">
          재무회계 · 임금관리 · 공단시스템 연동을 AI로 자동화해
          센터장님의 시간을 아껴드립니다.
        </p>
        
        <!-- 로그인 상태에 따른 버튼 영역 -->
        <div v-if="!isLoggedIn" class="d-flex ga-3 flex-wrap justify-center">
          <v-btn color="primary" size="large" @click="openLoginPage" rounded="lg">지금 시작하기</v-btn>
          <v-btn variant="tonal" size="large" to="/contact" rounded="lg">데모 문의</v-btn>
        </div>
        
        <div v-else class="d-flex flex-column align-center ga-3">
          <div class="text-h6 text-white font-weight-medium mb-2">
            환영합니다!
          </div>
          <div class="d-flex ga-3 flex-wrap justify-center">
            <v-btn color="primary" size="large" @click="goToDashboard" rounded="lg">서비스 시작하기!</v-btn>
            <v-btn variant="tonal" color="white" size="large" @click="handleLogout" rounded="lg">로그아웃</v-btn>
          </div>
        </div>
      </div>
    </v-img>

    <!-- Features -->
    <v-container class="py-12">
      <v-row class="mb-2">
        <v-col cols="12" class="text-center">
          <h2 class="text-h5 text-md-h4 font-weight-bold mb-2">센터 운영을 위한 핵심 기능</h2>
          <p class="text-body-2 text-medium-emphasis">복잡한 인증 절차 없이, 클릭 몇 번으로 끝.</p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <v-card variant="outlined" class="feature-card h-100">
            <v-img :src="cityImage" height="160" cover class="rounded-t" />
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
                <span class="text-subtitle-1 font-weight-medium">재무회계 자동화</span>
              </div>
              <div class="text-body-2 text-medium-emphasis">
                수입/지출 관리, 결의서 정리, 월마감까지 자동으로 정리합니다.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card variant="outlined" class="feature-card h-100">
            <v-img :src="jobImage" height="160" cover class="rounded-t" />
            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-clipboard-check-outline</v-icon>
                <span class="text-subtitle-1 font-weight-medium">임금관리 간소화</span>
              </div>
              <div class="text-body-2 text-medium-emphasis">
                근무표·수당·공제 계산을 규정에 맞춰 자동 산출합니다.
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card variant="outlined" class="feature-card h-100">
            <v-img :src="heroImage" height="160" cover class="rounded-t" />

            <v-card-text>
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
                <span class="text-subtitle-1 font-weight-medium">공단시스템 연동</span>
              </div>
              <div class="text-body-2 text-medium-emphasis">
                공단 양식과 데이터 연동으로 입력·제출 과정을 단순화합니다.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- CTA -->
    <v-container class="py-10 py-md-14">
      <v-sheet class="cta rounded-lg px-6 py-10 text-center mx-auto" elevation="0" max-width="1080">
        <h3 class="text-h6 text-md-h5 font-weight-bold mb-2">업무는 단순하게, 케어는 더 깊게</h3>
        <p class="text-body-2 text-medium-emphasis mb-6">오늘부터 반복 작업을 줄이고, 중요한 일에 집중해보세요.</p>
        <v-btn 
          v-if="!isLoggedIn"
          color="primary" 
          size="large" 
          rounded="lg" 
          @click="openLoginPage"
        >
          무료로 시작하기
        </v-btn>
        <v-btn 
          v-else
          color="primary" 
          size="large" 
          rounded="lg" 
          @click="goToDashboard"
        >
          서비스 시작하기!
        </v-btn>
      </v-sheet>
    </v-container>
  </div>
  
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

// 이미지 경로를 올바른 방식으로 설정
const heroImage = ref(new URL('../assets/images/장기요양지원센터 메인.jpg', import.meta.url).href);
const logo = ref(new URL('../assets/images/hypehive-logo.png', import.meta.url).href);
const jobImage = ref(new URL('../assets/images/job.jpg', import.meta.url).href);
const cityImage = ref(new URL('../assets/images/main_city2.png', import.meta.url).href);
const tableImage = ref(new URL('../assets/images/table.png', import.meta.url).href);

// 새창 참조를 저장할 변수
let loginWindow: Window | null = null;

// 로그인 상태와 사용자 정보
const isLoggedIn = computed(() => store.getters.isLoggedIn);
const currentUser = computed(() => store.getters.currentUser);
const isAdmin = computed(() => store.getters.isAdmin);

// 로그인 페이지를 새창으로 열기
const openLoginPage = () => {
  // 이미 열린 창이 있고 닫히지 않았으면 포커스
  if (loginWindow && !loginWindow.closed) {
    loginWindow.focus();
    return;
  }
  
  // 새창 열기 (화면 최대 크기)
  const screenWidth = window.screen.availWidth;
  const screenHeight = window.screen.availHeight;
  loginWindow = window.open('/pgm/login', '_blank', `width=${screenWidth},height=${screenHeight},scrollbars=yes,resizable=yes`);
  
  // 창이 닫힐 때 참조 초기화
  if (loginWindow) {
    loginWindow.addEventListener('beforeunload', () => {
      loginWindow = null;
    });
  }
};

// 대시보드로 이동 (새창으로)
const goToDashboard = () => {
  // 이미 열린 창이 있고 닫히지 않았으면 포커스
  if (loginWindow && !loginWindow.closed) {
    loginWindow.focus();
    return;
  }
  
  // 사용자 역할에 따라 경로 결정 (admin이면 관리자 페이지, user면 센터 페이지)
  const targetPath = isAdmin.value ? '/admin/members' : '/pgm/wage/WageProcess';
  
  // 새창으로 대시보드 열기
  loginWindow = window.open(targetPath, '_blank', 'width=1600,height=800,scrollbars=yes,resizable=yes');
  
  // 창이 닫힐 때 참조 초기화
  if (loginWindow) {
    loginWindow.addEventListener('beforeunload', () => {
      loginWindow = null;
    });
  }
};

// 로그아웃 처리
const handleLogout = async () => {
  try {
    await store.dispatch('performLogout');
    // 로그아웃 후 페이지 새로고침하여 상태 업데이트
    window.location.reload();
  } catch (error) {
    console.error('로그아웃 오류:', error);
  }
};

// 컴포넌트 마운트 시 인증 상태 확인
onMounted(async () => {
  await store.dispatch('validateAuth');
});
</script>

<style scoped>
@import '../assets/styles/indexPage.scss';


</style>